import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import axios from 'axios';
import { FaEdit, FaRegEye } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { Modal } from 'flowbite-react';
import def from '../../img/default.png'
import Swal from 'sweetalert2';

const RegisteredPatients = () => {
    const [patientsData, setPatientsData] = useState([]);
    const [previewModal, setPreviewModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [resetPassword, setResetPassword] = useState({
        adminPassword: '',
        newPassword: '',
        confirmPassword: '',
        patientID: ''
    });
    const [passwordModal, setPasswordModal] = useState(false);
    const [formData, setFormData] = useState({
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        gender: '',
        dob: '',
        profileImage: null,
        nic: '',
        password: ''
    });
    const handleModalClose = () => {
        setPasswordModal(false);
    }
    const handleResetChanges = (e) => {
        const { name, value } = e.target;
        setResetPassword({ ...resetPassword, [name]: value })
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                profileImage: file,
                previewImage: URL.createObjectURL(file),
            }));
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        const isFile = formData.profileImage && typeof formData.profileImage !== 'string';

        if (isFile && !allowedTypes.includes(formData.profileImage.type)) {
            toast.error("Only JPG, JPEG, and PNG formats are allowed");
            return;
        }

        const payload = new FormData();
        payload.append('id', formData.id);
        payload.append('firstName', formData.firstName);
        payload.append('lastName', formData.lastName);
        payload.append('email', formData.email);
        payload.append('phone', formData.phone);
        payload.append('address', formData.address);
        payload.append('gender', formData.gender);
        payload.append('dob', formData.dob);
        payload.append('nic', formData.nic);

        if (isFile) {
            payload.append('profileImage', formData.profileImage);
        } else {
            payload.append('existingImage', formData.profileImage);
        }
        try {
            const response = await axios.patch('https://backend.amarasinghemedicare.com/patient/update/patient', payload,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                }
            );

            if (response.status === 200) {
                toast.success("Profile has been updated");
                fetchPatients();
                setFormData({
                    id: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    address: '',
                    gender: '',
                    dob: '',
                    profileImage: null,
                    nic: '',
                    password: ''
                });
                setOpenModal(false);
            }
        } catch (error) {
            toast.error("Failed to update patient!");
            console.error("Error:", error);
        }
    }

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEditMode(false);
        setFormData({
            id: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            address: '',
            gender: '',
            dob: '',
            profileImage: null,
            nic: '',
            password: '',
        });
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('adminToken');

        if (!token) {
            toast.error("You are not logged in!");
            return;
        }

        if (!resetPassword.adminPassword || !resetPassword.newPassword || !resetPassword.confirmPassword) {
            toast.error("All fields are required!");
            return;
        }

        if (resetPassword.newPassword !== resetPassword.confirmPassword) {
            toast.error("New password and confirm password do not match!");
            return;
        }

        try {
            const response = await axios.patch('https://backend.amarasinghemedicare.com/admin/patient/password', resetPassword, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 200) {
                toast.success(response.data.message);
                setResetPassword({
                    adminPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                setIsEditMode(false);
                setOpenModal(false);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Error changing password:', error);
            toast.error("Failed to change password. Please try again.");
        }
    }

    const fetchPatients = async () => {
        try {
            const response = await axios.get('https://backend.amarasinghemedicare.com/patient/allPatients');
            if (response.data.success) {
                setPatientsData(response.data.users);
            } else {
                toast.error(response.data.message || "Failed to fetch patients");
            }
        } catch (error) {
            console.error("Error fetching patients:", error);
        }
    };

    useEffect(() => {
        fetchPatients();
    }, []);

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#202643',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '13px',
            },
        },
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(`https://backend.amarasinghemedicare.com/patient/delete/${id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
                        },
                    }
                );

                if (response.status === 200) {
                    toast.success(response.data.message);
                    fetchPatients();
                } else {
                    toast.error('Something went wrong');
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Delete failed. Please try again.');
            }
        }
    };

    const columns = [
        { name: 'Patient ID', selector: row => row.id, sortable: true },
        { name: 'Name', selector: row => row.firstName, sortable: true },
        { name: 'Email', selector: row => row.email, sortable: true },
        { name: 'NIC', selector: row => row.nic, sortable: true },
        { name: 'Contact', selector: row => row.phone, sortable: true },
        {
            name: 'Action',
            cell: row => (
                <div className="flex items-center justify-center space-x-3">
                    <button onClick={() => { setSelectedPatient(row); setPreviewModal(true); }} className="p-[6px] rounded-md bg-blue text-white hover:bg-red-500">
                        <FaRegEye />
                    </button>
                    <button
                        onClick={() => {
                            setIsEditMode(true);
                            setFormData({
                                id: row.id,
                                firstName: row.firstName,
                                lastName: row.lastName,
                                email: row.email,
                                phone: row.phone,
                                address: row.address,
                                gender: row.gender,
                                dob: row.dob.slice(0, 10),
                                profileImage: row.profileImage,
                                nic: row.nic,
                                password: '',
                            });
                            setOpenModal(true);
                        }}
                        className="p-[6px] rounded-md bg-green text-white hover:bg-green-500">
                        <FaEdit />
                    </button>
                    <button onClick={() => { handleDelete(row.id) }} className="p-[6px] rounded-md bg-red text-white hover:bg-red-500">
                        <MdDelete />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div>
            <Toaster />
            <div className="flex">
                <Link to='/register'
                    className="text-[16px] text-white uppercase font-semibold text-left bg-[#97CE4F] p-2 rounded-lg transform transition-transform duration-300 hover:scale-105"
                >
                    Register New Patient
                </Link>
            </div>

            <div className='h-fit p-[1px] mt-2 bg-[#202643]'>
                <DataTable
                    columns={columns}
                    data={patientsData}
                    fixedHeader
                    fixedHeaderScrollHeight="80vh"
                    className="data-table"
                    customStyles={customStyles}
                    pagination
                    paginationPerPage={20}
                    paginationRowsPerPageOptions={[20]}
                />
            </div>

            {previewModal && selectedPatient && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white border-[4px] border-green p-6 rounded-md w-[90%] md:w-[500px] max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-semibold mb-4">Registered Patient <span className='text-red'>Details</span></h2>
                        <div className="">
                            <div>
                                <strong>Profile Image:</strong><br />
                                <img
                                    src={`http://localhost:8081${selectedPatient.profileImage}`}
                                    alt="Profile"
                                    className="w-32 h-32 object-cover mt-2 rounded-full border-black border-[1px]"
                                />
                            </div>

                            <table class="table-auto border border-gray-400 border-collapse w-full shadow-md mt-2">
                                <tbody>
                                    <tr class="border-b border-gray-300">
                                        <td class="p-2 border border-gray-300 font-semibold">Full Name</td>
                                        <td class="p-2 border border-gray-300">{selectedPatient.firstName} {selectedPatient.lastName}</td>
                                    </tr>
                                    <tr class="border-b border-gray-300">
                                        <td class="p-2 border border-gray-300 font-semibold">Email</td>
                                        <td class="p-2 border border-gray-300">{selectedPatient.email}</td>
                                    </tr>
                                    <tr class="border-b border-gray-300">
                                        <td class="p-2 border border-gray-300 font-semibold">NIC No</td>
                                        <td class="p-2 border border-gray-300">{selectedPatient.nic}</td>
                                    </tr>
                                    <tr class="border-b border-gray-300">
                                        <td class="p-2 border border-gray-300 font-semibold">Contact Number</td>
                                        <td class="p-2 border border-gray-300">{selectedPatient.phone}</td>
                                    </tr>
                                    <tr class="border-b border-gray-300">
                                        <td class="p-2 border border-gray-300 font-semibold">Date of Birth</td>
                                        <td class="p-2 border border-gray-300">
                                            {new Date(selectedPatient.dob).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </td>
                                    </tr>
                                    <tr class="border-b border-gray-300">
                                        <td class="p-2 border border-gray-300 font-semibold">Gender</td>
                                        <td class="p-2 border border-gray-300">{selectedPatient.gender}</td>
                                    </tr>
                                    <tr class="border-b border-gray-300">
                                        <td class="p-2 border border-gray-300 font-semibold">Address</td>
                                        <td class="p-2 border border-gray-300">{selectedPatient.address}</td>
                                    </tr>
                                    <tr>
                                        <td class="p-2 border border-gray-300 font-semibold">Password</td>
                                        <td class="p-2 border border-gray-300">
                                            <button
                                                className='bg-blue px-3 font-light p-1 rounded-lg text-white'
                                                onClick={() => {
                                                    setResetPassword({
                                                        adminPassword: '',
                                                        newPassword: '',
                                                        confirmPassword: '',
                                                        patientID: selectedPatient.id
                                                    })
                                                    setPasswordModal(true)
                                                }
                                                }
                                            >Change Password
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-4 text-right">
                            <button
                                onClick={() => setPreviewModal(false)}
                                className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 bg-red"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Modal show={passwordModal} size='md' onClose={() => setPasswordModal(false)} popup>
                <Modal.Header />
                <Modal.Body>
                    <div className="space-y-6">
                        <h3 className="text-xl font-medium text-gray-900"><span className='text-red'>Change</span> Admin Password</h3>
                        <form onSubmit={handlePasswordChange}>
                            <div>
                                <label htmlFor="" className='text-xs'>Admin Password</label>
                                <input type="password" id='adminPassword' name='adminPassword' value={resetPassword.adminPassword} onChange={handleResetChanges} className='w-[100%] h-9 bg-[#d8dad8] rounded-md p-3 text-xs' required />
                            </div>

                            <div>
                                <label htmlFor="" className='text-xs'>New Password</label>
                                <input type="password" id='newPassword' name='newPassword' value={resetPassword.newPassword} onChange={handleResetChanges} className='w-[100%] h-9 bg-[#d8dad8] rounded-md p-3 text-xs' required />
                            </div>

                            <div>
                                <label htmlFor="" className='text-xs'>Confirm New Password</label>
                                <input type="password" id='confirmPassword' name='confirmPassword' value={resetPassword.confirmPassword} onChange={handleResetChanges} className='w-[100%] h-9 bg-[#d8dad8] rounded-md p-3 text-xs' required />
                            </div>

                            <div className='flex justify-end items-center h-10 mt-5 gap-3'>
                                <button onClick={handleModalClose} className='text-[16px] text-white uppercase font-semibold text-left bg-red p-2 rounded-lg transform transition-transform duration-300 hover:scale-105'>Cancel</button>
                                <button type='submit' className='text-[16px] text-white uppercase font-semibold text-left bg-[#97CE4F] p-2 rounded-lg transform transition-transform duration-300 hover:scale-105'>Save</button>
                            </div>
                        </form>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={openModal} size='md' onClose={() => handleCloseModal()} popup>
                <Modal.Header />
                <Modal.Body>
                    <form
                        onSubmit={handleEditSubmit}
                        className='w-full space-y-4'
                    >
                        <div className=''>
                            <h3 className="text-xl font-medium text-gray-900 mb-4">{isEditMode ? 'Update' : 'Register'} <span className='text-red'>Registered</span> Patient</h3>
                            <label htmlFor="firstName" className="table-row text-[#261C53] text-[15px]">First Name:</label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                                className="w-full p-3 bg-gray-200 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                        </div>

                        <div>
                            <label htmlFor="lastName" className="table-row text-[#261C53] text-[15px]">Last Name:</label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                                className="w-full p-3 bg-gray-200 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                        </div>

                        <div>
                            <label htmlFor="nic" className="table-row text-[#261C53] text-[15px]">NIC:</label>
                            <input
                                type="text"
                                id="nic"
                                name="nic"
                                value={formData.nic}
                                onChange={handleChange}
                                required
                                className="w-full p-3 bg-gray-200 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                        </div>

                        <div>
                            <label htmlFor="email" className="table-row text-[#261C53] text-[15px]">Email:</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full p-3 bg-gray-200 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                        </div>

                        <div>
                            <label htmlFor="phone" className="table-row text-[#261C53] text-[15px]">Contact Number:</label>
                            <input
                                type="text"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                                className="w-full p-3 bg-gray-200 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                        </div>

                        <div className='flex justify-center items-center gap-3 w-full'>
                            <div className='flex-1'>
                                <label htmlFor="dob" className="table-row text-[#261C53] text-[15px]">Date of Birth:</label>
                                <input
                                    type="date"
                                    id="dob"
                                    name="dob"
                                    value={formData.dob}
                                    onChange={handleChange}
                                    required
                                    className="w-full p-3 bg-gray-200 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                            </div>

                            <div className="flex-1">
                                <label htmlFor="gender" className="table-row text-[#261C53] text-[15px]">Gender:</label>
                                <div className="flex justify-between w-full p-3 bg-gray-200 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400">
                                    <input
                                        type="radio"
                                        name="gender"
                                        value="female"
                                        checked={formData.gender === "female"}
                                        onChange={handleChange}
                                        required
                                        className=''
                                    /> Female
                                    <input
                                        type="radio"
                                        name="gender"
                                        value="male"
                                        checked={formData.gender === "male"}
                                        onChange={handleChange}
                                        required
                                        className=""
                                    /> Male
                                </div>
                            </div>
                        </div>

                        {isEditMode ? (
                            <img
                                src={
                                    formData.previewImage
                                        ? formData.previewImage
                                        : formData.profileImage && typeof formData.profileImage === 'string'
                                            ? `http://localhost:8081${formData.profileImage}`
                                            : def
                                }
                                alt="Profile"
                                className="w-32 h-32 object-cover mt-2 rounded-full border-black border-[1px]"
                            />
                        ) : ''
                        }

                        <div className="">
                            <label htmlFor="profileImage" className="table-row text-[#261C53] text-[15px]">Profile Image:</label>
                            <input
                                type="file"
                                id="profileImage"
                                name="profileImage"
                                accept="profileImage_"
                                onChange={handleFileChange}
                                className="w-full p-3 bg-gray-200 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                        </div>

                        <div>
                            <div>
                                <label htmlFor="address" className="table-row text-[#261C53] text-[15px]">Address:</label>
                                <input
                                    type="text"
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    required
                                    className="w-full p-3 bg-gray-200 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                            </div>
                        </div>

                        <div className="">
                            {isEditMode ? '' : (
                                <div className="mt-4">
                                    <label htmlFor="password" className="table-row text-[#261C53] text-[15px]">Password:</label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="w-full p-3 bg-gray-200 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    />
                                </div>
                            )}
                            <div className='flex justify-end items-center h-10 mt-5 gap-3'>
                                <button
                                    type="submit"
                                    className="text-[16px] text-white uppercase font-semibold text-left bg-[#97CE4F] p-2 rounded-lg transform transition-transform duration-300 hover:scale-105"
                                >
                                    {isEditMode ? 'Update' : 'Submit'}
                                </button>
                            </div>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
        </div>
    )
}

export default RegisteredPatients;
